import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GrowthOSWordmark } from "@/components/Logo";
import { useApp } from "@/context/AppContext";
import { AlertCircle, PlayCircle, ShieldCheck } from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { CgMicrosoft } from "react-icons/cg";
import { clearOAuthReturnPath, rememberOAuthReturnPath, safeReturnPath } from "@/workspaceRouting.mjs";

// Sign-in is Google or Microsoft only. Password sign-in was retired on 2026-10-26: nobody used it,
// and the auth project's Email provider is off, so the browser never offers it.
const PROVIDER_BUTTON =
  "flex w-full items-center justify-center gap-2.5 rounded-full border border-white/20 bg-white/[0.06] py-3 text-xs font-extrabold uppercase tracking-[0.09em] text-white transition-colors hover:bg-white/[0.12] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sm-accent disabled:cursor-wait disabled:opacity-55";

export default function Login() {
  const {
    authStatus,
    authError,
    authProviders,
    retryAuth,
    signInWithGoogle,
    signInWithMicrosoft,
    demoAvailable,
    enterDemoView,
  } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = safeReturnPath(location.state?.from) || "/";
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (["authenticated", "select-client", "access-denied"].includes(authStatus)) {
      clearOAuthReturnPath();
      navigate(returnPath, { replace: true });
    }
  }, [authStatus, navigate, returnPath]);

  const startProvider = async (start, label) => {
    setLoading(true);
    setFormError("");
    try {
      rememberOAuthReturnPath(returnPath);
      await start();
    } catch (error) {
      clearOAuthReturnPath();
      setFormError(error?.message || `${label} sign in could not start.`);
      setLoading(false);
    }
  };
  const google = () => startProvider(signInWithGoogle, "Google");
  const microsoft = () => startProvider(signInWithMicrosoft, "Microsoft");

  const busy = loading || authStatus === "loading" || authStatus === "error";
  const providersKnown = authStatus !== "loading" && authStatus !== "error";
  const offersGoogle = authProviders.includes("google");
  const offersMicrosoft = authProviders.includes("azure");

  return (
    <div className="mc-bg relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-12">
      <div className="absolute inset-0 mc-grid opacity-50" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(800px 500px at 50% 15%, rgba(70,144,217,0.16), transparent 60%), radial-gradient(600px 400px at 50% 90%, rgba(2,184,117,0.08), transparent 60%)",
        }}
      />

      <div className="relative w-full max-w-md fade-up">
        <div className="mb-8 text-center">
          <GrowthOSWordmark className="text-5xl" />
          <div className="label-eyebrow mt-3 text-sm-accent">Command Center</div>
        </div>

        <div className="glass-dark rounded-2xl p-8 shadow-[0_40px_100px_-30px_rgba(0,59,141,0.5)]">
          <h1 className="font-display text-2xl font-bold leading-tight text-white">
            Visibility and control for your brand's AI operating system.
          </h1>
          <p className="mt-2 text-sm text-white/60">
            Monitor your agent, connected systems, improvement requests, and operational activity in one place.
          </p>

          <section aria-labelledby="login-methods-heading" className="mt-7 space-y-3.5">
            <h2 id="login-methods-heading" className="text-xs font-semibold uppercase tracking-[0.09em] text-white/50">
              Sign in with your work account
            </h2>

            {authStatus === "loading" ? (
              <p role="status" className="rounded-lg border border-white/10 px-3 py-2.5 text-xs text-white/55" data-testid="login-loading">
                Loading sign-in options…
              </p>
            ) : null}

            {offersGoogle ? (
              <button
                data-testid="login-google-btn"
                type="button"
                onClick={google}
                disabled={busy}
                className={PROVIDER_BUTTON}
              >
                <SiGoogle size={13} aria-hidden /> Continue with Google
              </button>
            ) : null}

            {offersMicrosoft ? (
              <button
                data-testid="login-microsoft-btn"
                type="button"
                onClick={microsoft}
                disabled={busy}
                className={PROVIDER_BUTTON}
              >
                <CgMicrosoft size={13} aria-hidden /> Continue with Microsoft
              </button>
            ) : null}

            {providersKnown && !offersGoogle && !offersMicrosoft ? (
              <p role="status" className="rounded-lg border border-white/10 px-3 py-2.5 text-xs text-white/55" data-testid="login-no-provider">
                No sign-in method is available right now. Ask your GrowthOS team for access.
              </p>
            ) : null}

            {(formError || (authStatus === "error" && authError?.message)) && (
              <div className="flex items-start gap-2 rounded-lg border border-[#EF4343]/25 bg-[#EF4343]/10 px-3 py-2.5 text-xs leading-relaxed text-[#FCA5A5]" role="alert">
                <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden />
                <div>
                  <span>{formError || authError.message}</span>
                  {authStatus === "error" ? (
                    <button type="button" onClick={retryAuth} className="mt-2 block font-semibold text-white underline underline-offset-2">Try loading authentication again</button>
                  ) : null}
                </div>
              </div>
            )}
          </section>

          <div className="mt-6 flex items-center gap-2 border-t border-white/10 pt-5 text-xs text-white/50">
            <ShieldCheck size={13} className="shrink-0" aria-hidden />
            Authentication and workspace access are verified server-side for every request.
          </div>

          {demoAvailable ? (
            <div className="mt-5 rounded-xl border border-sm-accent/25 bg-sm-accent/[0.08] p-4" data-testid="login-demo-panel">
              <div className="flex items-start gap-2.5">
                <PlayCircle size={15} className="mt-0.5 shrink-0 text-[#9cc6f0]" aria-hidden />
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-white">Demo view</div>
                  <p className="mt-1 text-xs leading-relaxed text-white/55">
                    Explore the dashboard with clearly labelled sample data. No workspace is opened, nothing is sent, and no
                    live source is read.
                  </p>
                </div>
              </div>
              <button
                data-testid="login-demo-btn"
                type="button"
                onClick={() => {
                  enterDemoView();
                  navigate("/", { replace: true });
                }}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-sm-accent/45 bg-white/[0.06] py-2.5 text-xs font-extrabold uppercase tracking-[0.09em] text-white transition-colors hover:bg-white/[0.12]"
              >
                <PlayCircle size={14} aria-hidden /> Open Demo View
              </button>
            </div>
          ) : null}
        </div>

        <div className="mt-5 text-center font-mono text-[11px] uppercase tracking-[0.18em] text-white/35" data-testid="secure-workspace-label">
          Secure GrowthOS client workspace
        </div>
      </div>
    </div>
  );
}
